import { el } from "../utils/dom.ts";
import { getHealthIndex, type HealthIndexAvailable, type HealthComponent } from "../services/api-client.ts";

// Health-gauge panel: a semicircular 0..100 gauge for the composite economic-health
// index, plus a per-component breakdown, the confidence band, and honest "N/8 active"
// + "thử nghiệm / không phải khuyến nghị" labelling. Renders into a provided host
// element (the #health-gauge-slot on the Tổng quan tab).
export class HealthGaugePanel {
  readonly el: HTMLElement;

  constructor() {
    this.el = el("section", { className: "panel health-gauge-panel", "data-panel": "health-gauge" }, [
      el("div", { className: "panel-loading" }, ["Đang tải…"]),
    ]);
  }

  async load(): Promise<void> {
    try {
      const data = await getHealthIndex();
      if (!data.available) {
        this.renderEmpty();
        return;
      }
      this.render(data);
    } catch {
      this.el.replaceChildren(el("div", { className: "panel-error" }, ["Không tải được chỉ số."]));
    }
  }

  private renderEmpty(): void {
    this.el.replaceChildren(
      el("div", { className: "panel-header" }, [
        el("span", { className: "panel-title" }, ["Chỉ số sức khỏe kinh tế"]),
      ]),
      el("p", { className: "muted" }, ["Đang tích lũy dữ liệu để tính chỉ số."]),
    );
  }

  private render(d: HealthIndexAvailable): void {
    const score = Math.round(d.score);
    this.el.replaceChildren(
      el("div", { className: "panel-header" }, [
        el("span", { className: "panel-title" }, [
          "Chỉ số sức khỏe kinh tế",
          el("span", { className: "panel-subtitle" }, [`thử nghiệm · ${d.active_count}/${d.total_components} trụ`]),
        ]),
      ]),
      this.gauge(score, d.band_low, d.band_high),
      this.breakdown(d.components),
      el("p", { className: "health-disclaimer" }, [
        `Chỉ số tổng hợp thử nghiệm từ ${d.active_count}/${d.total_components} thành phần có dữ liệu (50 = trung tính). Không phải dự báo hay khuyến nghị đầu tư.`,
      ]),
    );
  }

  // Semicircular gauge: 180° arc, needle at score. Band shown as a lighter arc span.
  private gauge(score: number, bandLow: number | null, bandHigh: number | null): HTMLElement {
    const W = 220;
    const H = 130;
    const cx = W / 2;
    const cy = H - 10;
    const r = 90;
    // Map 0..100 → 180°..0° (left to right).
    const angle = (v: number) => Math.PI - (Math.max(0, Math.min(100, v)) / 100) * Math.PI;
    const pt = (v: number, radius: number) => ({
      x: cx + radius * Math.cos(angle(v)),
      y: cy - radius * Math.sin(angle(v)),
    });
    const arcPath = (from: number, to: number, radius: number) => {
      const a = pt(from, radius);
      const b = pt(to, radius);
      const large = Math.abs(to - from) > 50 ? 1 : 0;
      // sweep 1 = clockwise in SVG y-down coords for our left→right arc
      return `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} A ${radius} ${radius} 0 ${large} 1 ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
    };
    const needle = pt(score, r - 14);
    const color = score >= 60 ? "#4ade80" : score >= 40 ? "#f5c518" : "#f87171";

    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    svg.setAttribute("class", "health-gauge-svg");
    const mk = (tag: string, attrs: Record<string, string>) => {
      const e = document.createElementNS(ns, tag);
      for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
      return e;
    };
    // Track arc.
    svg.append(mk("path", { d: arcPath(0, 100, r), fill: "none", stroke: "#1e2733", "stroke-width": "12", "stroke-linecap": "round" }));
    // Confidence band arc.
    if (bandLow != null && bandHigh != null) {
      svg.append(mk("path", { d: arcPath(bandLow, bandHigh, r), fill: "none", stroke: "rgba(124,196,255,0.35)", "stroke-width": "12", "stroke-linecap": "round" }));
    }
    // Needle + hub.
    svg.append(mk("line", { x1: String(cx), y1: String(cy), x2: needle.x.toFixed(1), y2: needle.y.toFixed(1), stroke: color, "stroke-width": "3", "stroke-linecap": "round" }));
    svg.append(mk("circle", { cx: String(cx), cy: String(cy), r: "5", fill: color }));

    return el("div", { className: "health-gauge-wrap" }, [
      svg,
      el("div", { className: "health-score", style: `color:${color}` }, [String(score)]),
      bandLow != null && bandHigh != null
        ? el("div", { className: "health-band-label" }, [`Dải tin cậy ${Math.round(bandLow)}–${Math.round(bandHigh)}`])
        : el("div", {}, []),
    ]);
  }

  private breakdown(components: HealthComponent[]): HTMLElement {
    if (components.length === 0) {
      return el("p", { className: "muted" }, ["Chưa có thành phần nào đủ dữ liệu."]);
    }
    const rows = components.map((c) => {
      // contribution > 0 pushes score up (healthier); color accordingly.
      const good = (c.contribution ?? 0) >= 0;
      const arrow = good ? "▲" : "▼";
      const zStr = c.z === null ? "—" : c.z.toFixed(2);
      return el("div", { className: "health-comp-row" }, [
        el("span", { className: "health-comp-label" }, [c.label]),
        el("span", { className: `health-comp-z ${good ? "up" : "down"}` }, [`${arrow} z=${zStr}`]),
      ]);
    });
    return el("div", { className: "health-breakdown" }, rows);
  }
}
